import { useQuery } from '@apollo/client';
import { isWeekend } from 'date-fns';
import { get } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AIResolvedFieldsMap } from '../../../production/resource/orders/utils/resourceOrderEditor';
import { SCHEDULE_WIDGET_DISABLED_UNITS } from '../../../scheduler/schedulerConstants';
import { getDecimalsForDurationUnit } from '../../../supply/booking/ScheduleCalculator';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { searchClient, slimResultsQuery } from '../../list/slimQuery';
import { DateRange, parseApiDate } from '../../utils/dateTime';
import { FormContext } from '../FormContext';
import FormItem from '../FormItem';
import FormWidget from '../FormWidget';
import { AutocompleteOption } from './OptionRendererProps';
import { DEFAULT_DURATION, EMPTY_SCHEDULE } from './scheduleConstants';

const showTimeForUnits = ['hour'];

export { DEFAULT_DURATION, EMPTY_SCHEDULE };

type SchedulerProps = {
    name: string;
    allowedUnits?: string[];
    showFullSchedule?: boolean;
    isMilestone?: boolean;
    disableWeekends?: boolean;
    disabled?: boolean;
    prefix?: React.ReactElement;
    disableDates?: boolean;
    disableUnits?: boolean;
    locale?: string;
    showDateRange?: boolean;
    takeFullWidth?: boolean;
    isAIExtractedData?: boolean;
    updateAIDataResolved?: (name: string) => void;
    aiFieldResolvedState?: AIResolvedFieldsMap;
    disabledUnits?: string[];
    deferKeyboardDateCommitUntilBlur?: boolean;
};

export default function ScheduleWidget(props: SchedulerProps) {
    const {
        name,
        allowedUnits,
        disableWeekends,
        showFullSchedule,
        disabled,
        prefix,
        disableDates,
        disableUnits,
        isMilestone = false,
        locale,
        showDateRange,
        takeFullWidth = false,
        isAIExtractedData = false,
        updateAIDataResolved = undefined,
        aiFieldResolvedState = null,
        disabledUnits,
        deferKeyboardDateCommitUntilBlur = false
    } = props;
    const [unitOptions, setUnitOptions] = useState([]);
    const { state, handleChange } = useContext(FormContext);
    const dateRange: DateRange = get(state, name);
    const durationSpecified = dateRange && dateRange.durationSpecified;
    const timeDisabled = !showTimeForUnits.includes(dateRange?.dateTimeDuration?.unit);
    const unitName = `${name}.dateTimeDuration.unit`;
    const currentUnitType = get(state, unitName);

    const shouldDisableDate = (day: Date): boolean => {
        return disableWeekends ? isWeekend(day) : false;
    };

    const endDateReference = useMemo(() => parseApiDate(dateRange?.start), [dateRange?.start]);

    const { data, loading } = useQuery(slimResultsQuery('Unit'), {
        client: searchClient,
        variables: {
            filters: [
                { identifier: 'entity', value: 'Unit' },
                { identifier: 'measure', value: 'Time' }
            ]
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: true,
        canonizeResults: true
    });

    const mergedDisabledUnits = useMemo(() => {
        return disabledUnits?.length
            ? [...new Set([...SCHEDULE_WIDGET_DISABLED_UNITS, ...disabledUnits])]
            : SCHEDULE_WIDGET_DISABLED_UNITS;
    }, [disabledUnits]);

    useEffect(() => {
        if (data && data.results.hits && data.results.hits.items) {
            let isCurrentValid = false;
            const allowed = allowedUnits ?? [];
            const restricted = mergedDisabledUnits ?? [];

            const items = data.results.hits.items.filter(
                (item) => (allowed.length === 0 || allowed.includes(item.name)) && !restricted.includes(item.name)
            );
            const unitOpts: Array<AutocompleteOption> = items.map((item) => {
                // If current unit type is found in allowed list we won't be setting a default.
                if (currentUnitType === item.name) {
                    isCurrentValid = true;
                }
                return { id: item.name, label: item.label };
            });
            setUnitOptions(unitOpts);

            if (unitOpts.length && !isCurrentValid) {
                handleChange(unitName, unitOpts[0].id);
            }
        }
    }, [data, allowedUnits, handleChange, currentUnitType, mergedDisabledUnits, unitName]);

    const currentUnit = dateRange?.dateTimeDuration?.unit;
    const decimals = getDecimalsForDurationUnit(currentUnit);
    const unitComponent = (
        <FormWidget
            disabled={disabled || disableUnits}
            label={'Unit'}
            name={`${name}.dateTimeDuration.unit`}
            component="Autocomplete"
            loading={loading}
            options={unitOptions}
            disableClearable
            isAIExtractedData={isAIExtractedData && !aiFieldResolvedState?.[`${name}.dateTimeDuration.unit`]}
            updateAIDataResolved={updateAIDataResolved}
            locale={locale}
        />
    );
    const startDateComponent = (
        <FormWidget
            disabled={disabled || disableDates}
            component="Date"
            name={`${name}.start`}
            label="Start date"
            shouldDisableDate={shouldDisableDate}
            deferKeyboardDateCommitUntilBlur={deferKeyboardDateCommitUntilBlur}
            locale={locale}
            isAIExtractedData={!!isAIExtractedData && !!dateRange?.start && !aiFieldResolvedState?.[`${name}.start`]}
            updateAIDataResolved={updateAIDataResolved}
        />
    );
    const startTimeComponent = (
        <FormWidget
            disabled={timeDisabled || disabled || disableDates}
            component="Time"
            name={`${name}.startTime`}
            label="Start time"
            locale={locale}
        />
    );
    const durationComponent = (
        <FormWidget
            disabled={!durationSpecified || disabled}
            component={'Number'}
            decimals={decimals}
            name={`${name}.dateTimeDuration.value`}
            label="Duration"
            forcePositive={true}
            locale={locale}
            useGrouping={false}
        />
    );
    const endDateComponent = (
        <FormWidget
            disabled={durationSpecified || disabled || disableDates}
            component="Date"
            name={`${name}.end`}
            label="End date"
            referenceDate={endDateReference}
            shouldDisableDate={shouldDisableDate}
            deferKeyboardDateCommitUntilBlur={deferKeyboardDateCommitUntilBlur}
            locale={locale}
            isAIExtractedData={
                !!isAIExtractedData && !!dateRange?.end && !aiFieldResolvedState?.[`${name}.end`] && !durationSpecified
            }
            updateAIDataResolved={updateAIDataResolved}
        />
    );
    const endTimeComponent = (
        <FormWidget
            disabled={durationSpecified || timeDisabled || disabled || disableDates}
            component="Time"
            name={`${name}.endTime`}
            label="End time"
            locale={locale}
        />
    );

    /*
        dur=true
        unit, duration, startDate (2, 2, 2)
        dur=true, unit=hour
        unit, duration, startDate, startTime (2, 2, 2, 2)

        dur=false
        unit, startDate, endDate (2, 2, 3)
        dur=false, unit=hour
        unit, startDate, startTime, endDate, endTime (2, 2, 2, 2, 2)
    */
    const hasPrefix = !!prefix;
    return isMilestone ? (
        <FormItem span={!props.takeFullWidth ? 6 : 12}>{startDateComponent}</FormItem>
    ) : showDateRange ? (
        takeFullWidth ? (
            <FormItem span={12} sx={{ width: '100%' }}>
                {startDateComponent}
                {endDateComponent}
            </FormItem>
        ) : (
            <>
                <FormItem span={6}>{startDateComponent}</FormItem>
                <FormItem span={6}>{endDateComponent}</FormItem>
            </>
        )
    ) : showFullSchedule ? (
        <>
            <FormItem span={6}>{durationComponent}</FormItem>
            <FormItem span={6}>{unitComponent}</FormItem>

            <FormItem span={6}>
                {startDateComponent}
                {timeDisabled ? <></> : startTimeComponent}
            </FormItem>

            <FormItem span={6}>
                {endDateComponent}
                {timeDisabled ? <></> : endTimeComponent}
            </FormItem>
        </>
    ) : (
        <>
            {prefix && <FormItem span={2}>{prefix}</FormItem>}
            <FormItem span={2}>{unitComponent}</FormItem>

            <FormItem span={hasPrefix ? 4 : 5}>
                {startDateComponent}
                {timeDisabled ? <></> : startTimeComponent}
            </FormItem>

            {durationSpecified ? (
                <FormItem span={2}>{durationComponent}</FormItem>
            ) : (
                <FormItem span={hasPrefix ? 4 : 5}>
                    {endDateComponent}
                    {timeDisabled ? <></> : endTimeComponent}
                </FormItem>
            )}
        </>
    );
}
